import Image from '../../../Utils/Image/image-component';
import { Eyebrow, Heading, Cta } from './home-ui';

/** Reusable striped placeholder for design images not yet uploaded. */
function MediaFrame({ src, alt, ratio = 'aspect-[16/9]', label }) {
    if (src) {
        return (
            <Image src={src} alt={alt} useSekeleton objectFit="cover"
                wrapperClassName={`w-full ${ratio} rounded-card overflow-hidden`} />
        );
    }
    return (
        <div className={`flex w-full ${ratio} items-center justify-center rounded-card border border-surface-line text-center text-xs font-medium uppercase tracking-wide text-ink-faint`}
            style={{ backgroundImage: 'repeating-linear-gradient(45deg,#F2F4F7 0 10px,#E7EAF0 10px 20px)' }}>
            <span className="rounded-full bg-white/80 px-3 py-1">{label || alt}</span>
        </div>
    );
}

export default function Hero({ data }) {
    if (!data) return null;
    const { eyebrow, titleLine1, titleLine2, paragraph, chips = [], stats = [], primaryCta, secondaryCta, image, imageAlt } = data;

    return (
        <section className="bg-white pt-10 pb-[72px]">
            <div className="mx-auto grid w-full max-w-[1200px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
                {/* Left: copy */}
                <div>
                    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                    <h1 className="mt-4 font-display text-[40px] font-bold leading-[1.08] tracking-[-0.03em] text-navy sm:text-[58px]">
                        {titleLine1} <span className="text-brand">{titleLine2}</span>
                    </h1>
                    <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-muted">{paragraph}</p>

                    {chips.length > 0 && (
                        <div className="mt-6 flex flex-wrap gap-2">
                            {chips.map((c) => (
                                <span key={c} className="rounded-full border border-surface-line bg-surface-alt px-3.5 py-1.5 text-sm text-ink">{c}</span>
                            ))}
                        </div>
                    )}

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        {primaryCta && <Cta label={primaryCta.label} href={primaryCta.href} />}
                        {secondaryCta && <Cta label={secondaryCta.label} href={secondaryCta.href} variant="outline" />}
                    </div>
                </div>

                {/* Right: photo + orange stats block */}
                <div className="relative">
                    <MediaFrame src={image} alt={imageAlt} ratio="aspect-[4/3] lg:aspect-[16/10]" label="Hero photo, inspector / job site" />
                    {stats.length > 0 && (
                        <div className="mt-4 grid grid-cols-3 gap-px overflow-hidden rounded-card bg-brand text-white lg:absolute lg:-bottom-6 lg:right-6 lg:mt-0 lg:w-[70%]">
                            {stats.map((s) => (
                                <div key={s.label} className="bg-brand px-4 py-4">
                                    <div className="font-display text-lg font-bold leading-none">{s.label}</div>
                                    <div className="mt-1 text-[13px] leading-snug text-white/85">{s.sub}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export { MediaFrame };
